import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, Signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  MsalBroadcastService,
  MsalService
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus
} from '@azure/msal-browser';
import { BlockUIModule } from 'primeng/blockui';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { filter } from 'rxjs/operators';
import { AppUpdateComponent } from "./shared/components/app-update/app-update.component";
import { FooterComponent } from "./shared/components/footer/footer.component";
import { HeaderComponent } from "./shared/components/header/header.component";
import { HelpOverlayComponent } from "./shared/components/help-overlay/help-overlay.component";
import { LeftSidebarComponent } from "./shared/components/left-sidebar/left-sidebar.component";
import { ALLOWED_TENANT_IDS } from './shared/constants/app.constants';
import { DowntimeService } from './shared/services/downtime.service';
import { HelpDisplayService } from './shared/services/help-display.service';
import { HomeService } from './shared/services/home.service';
import { InsightsService } from './shared/services/insights.service';
import { LoadingService } from './shared/services/loading.service';
import { NavigationService } from './shared/services/navigation.service';
import { ThemeService } from './shared/services/theme.service';
import { UserService } from './shared/services/user.service';
import { VersionCheckService } from './shared/services/version-check.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  imports: [
    CommonModule,
    BlockUIModule,
    ProgressSpinnerModule,
    PanelModule,
    ToastModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    LeftSidebarComponent,
    HelpOverlayComponent,
    AppUpdateComponent
  ],
})
export class AppComponent implements OnInit {
  isIframe = false;
  loading: Signal<boolean>;
  authenticated: Signal<boolean>;
  showHelp: Signal<boolean>;
  isLogoutPage = false;
  pageTitle: Signal<string>;
  private destroyRef = inject(DestroyRef);

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private theme: ThemeService,
    private user: UserService,
    private loader: LoadingService,
    private insights: InsightsService,
    private help: HelpDisplayService,
    private router: Router,
    private downtime: DowntimeService,
    private navService: NavigationService,
    private versionCheck: VersionCheckService,
    private home: HomeService
  ) {
    this.authenticated = this.user.authenticated;
    this.loading = this.loader.loading;
    this.showHelp = this.help.visible;
    this.pageTitle = this.navService.pageTitle;
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(e => {
      this.isLogoutPage = ['/logged-out', '/not-authorized'].includes(e.url);
      this.isLogoutPage && this.loader.loading.set(false);
    });
  }

  ngOnInit(): void {
    this.theme.setDarkOrLightTheme('initial');
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/logged-out';
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const account = payload?.account || this.authService.instance.getAllAccounts()[0];

        if (!this.isAllowedTenant(account)) {
          console.error('Account details:', account);
          this.insights.trackTrace(`Rejected login from disallowed tenant: ${account?.tenantId}`, { component: 'app component' });
          this.authService.instance.logoutRedirect({
            account: account,
            postLogoutRedirectUri: '/not-authorized',
          });
          return;
        }
        this.authService.instance.setActiveAccount(account);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_FAILURE)
      )
      .subscribe((result: EventMessage) => {
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
    if (activeAccount) {
      if (!this.isAllowedTenant(activeAccount)) {
        console.error('DISALLOWED TENANT ID DETECTED:', activeAccount.tenantId);
        console.error('Account details:', activeAccount);
        this.insights.trackTrace(`Blocked cached account from disallowed tenant: ${activeAccount.tenantId}`, { component: 'app component' });
        this.authService.instance.logoutRedirect({
          account: activeAccount,
          postLogoutRedirectUri: '/not-authorized',
        });
        return;
      }
      this.insights.trackTrace(`${activeAccount.username} was authenticated`, { component: 'app component' });
      this.user.setUserInfo(activeAccount);
      this.downtime.getDowntime();
      this.home.authState.set(true);
    }
  }

  private isAllowedTenant(account: AccountInfo | null | undefined): boolean {
    return !!account?.tenantId && ALLOWED_TENANT_IDS.some(tenant => tenant.id === account.tenantId);
  }
}
